import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import MoreIcon from '../assets/icons/More.svg';
import StatusIcon from '../assets/icons/status.svg';
import TempIcon from '../assets/icons/temp.svg';
import HealthIcon from '../assets/icons/health.svg';
import BatteryIcon from '../assets/icons/battery.svg';
import EditIcon from '../assets/icons/edit.svg';

function Overview() {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const user =
    localStorage.getItem('username') ||
    sessionStorage.getItem('username') ||
    'demo';

  const batteryVoltages = [
    3.6, 3.591, 3.582, 3.573, 3.564, 3.555, 3.545, 3.536, 3.527, 3.518,
    3.509, 3.5, 3.491, 3.482, 3.473, 3.464, 3.455, 3.445, 3.436, 3.427,
    3.418, 3.409, 3.4, 3.391, 3.382, 3.373, 3.364, 3.355, 3.345, 3.336,
    3.327, 3.318, 3.309, 3.3, 3.291, 3.282, 3.273, 3.264, 3.255, 3.245,
    3.236, 3.227, 3.218, 3.209, 3.2, 3.191, 3.182, 3.173, 3.164, 3.155,
    3.145, 3.136, 3.127, 3.118, 3.109, 3.1, 3.091, 3.082, 3.073, 3.064,
    3.055, 3.045, 3.036, 3.027, 3.018, 3.009, 3.0, 2.991, 2.982, 2.973,
    2.964, 2.955, 2.945, 2.936, 2.927, 2.918, 2.909, 2.9, 2.891, 2.882,
    2.873, 2.864, 2.855, 2.845, 2.836, 2.827, 2.818, 2.809, 2.8, 2.791,
    2.782, 2.773, 2.764, 2.755, 2.745, 2.736, 2.727, 2.718, 2.709, 2.7,
    2.7,
  ];

  const voltageToPercent = (volt) => {
    if (!volt) return 0;
    const v = parseFloat(volt);
    for (let i = 0; i < batteryVoltages.length; i++) {
      const pct = 100 - i;
      if (v >= batteryVoltages[i]) return pct;
    }
    return 0;
  };

  const percentToColor = (pct) => {
    if (pct >= 60) return 'bg-gradient-to-r from-emerald-400 to-emerald-500';
    if (pct >= 53) return 'bg-gradient-to-r from-amber-300 to-amber-400';
    if (pct >= 50) return 'bg-gradient-to-r from-amber-200 to-amber-300';
    if (pct >= 28) return 'bg-gradient-to-r from-orange-400 to-orange-500';
    return 'bg-gradient-to-r from-rose-500 to-rose-600';
  };

  const cardVariants = useMemo(
    () => ({
      hidden: { opacity: 0, y: 20 },
      visible: { opacity: 1, y: 0 },
    }),
    []
  );

  useEffect(() => {
    const fetchDevices = () => {
      fetch(`/get_user_devices.php?username=${encodeURIComponent(user)}`)
        .then((res) => res.json())
        .then((data) => setDevices(Array.isArray(data) ? data : []))
        .catch(() => setDevices([]));
    };

    fetchDevices();
    const interval = setInterval(fetchDevices, 120000); // refresh every 2 minutes
    return () => clearInterval(interval);
  }, [user]);


  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#151C2A] via-[#0C1017] to-[#080B10]" />
        <div className="absolute -top-32 -right-24 h-72 w-72 rounded-full bg-primary/40 blur-3xl opacity-70" />
        <div className="absolute -bottom-24 -left-16 h-80 w-80 rounded-full bg-cyan-500/30 blur-3xl opacity-70" />
      </div>
      <div className="relative space-y-8">
        <div className="relative flex flex-wrap items-center gap-4">
          <div>
            <p className="text-sm uppercase tracking-[0.4em] text-white/60">Dashboard</p>
            <h1 className="text-4xl font-semibold text-white">Overview</h1>
          </div>
          <span className="ml-auto inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1 text-xs font-medium text-emerald-300 backdrop-blur-md">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-300" />
            </span>
            Live Monitoring
          </span>
          <button
            className="group flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 transition-all duration-200 hover:border-white/30 hover:bg-white/10"
            aria-label="More options"
          >
            <img
              src={MoreIcon}
              alt="More"
              className="h-5 w-5 opacity-70 transition-opacity duration-200 group-hover:opacity-100"
            />
          </button>
        </div>

        <motion.div
          layout
          className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3"
        >
          {devices.map((dev, idx) => {
            const pct = voltageToPercent(dev.battery_volt);
            return (
              <motion.div
                key={idx}
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.35, delay: idx * 0.05 }}
                onClick={() =>
                  navigate('/dashboard/unit', { state: { unit: dev.unit } })
                }
                className="group relative flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-white/20 hover:bg-white/10"
              >
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-white/50">Device</p>
                    <h2 className="text-lg font-semibold text-white">{dev.device_name}</h2>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/dashboard/add-device', { state: { device: dev } });
                    }}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-200 hover:bg-primary/80"
                    aria-label="Edit Device"
                  >
                    <img src={EditIcon} alt="Edit" className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex flex-1 flex-col gap-4 md:flex-row">
                  <div className="flex h-32 w-full items-center justify-center overflow-hidden rounded-xl bg-black/20 p-2 md:h-32 md:w-1/2">
                    <img
                      src={dev.image_path}
                      alt="Device"
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-2 text-sm text-white">
                    <div
                      className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold uppercase tracking-wide ${
                        dev.status === 'Online'
                          ? 'bg-emerald-500/20 text-emerald-300'
                          : 'bg-rose-500/20 text-rose-300'
                      }`}
                    >
                      <img src={StatusIcon} alt="" className="h-4 w-4" />
                      Status: {dev.status}
                    </div>
                    <div
                      className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold uppercase tracking-wide ${
                        dev.temp_status === 'Good'
                          ? 'bg-emerald-500/20 text-emerald-300'
                          : dev.temp_status === 'Warning'
                          ? 'bg-amber-500/20 text-amber-200'
                          : dev.temp_status === 'Failing'
                          ? 'bg-rose-500/20 text-rose-300'
                          : 'bg-slate-500/20 text-slate-200'
                      }`}
                    >
                      <img src={TempIcon} alt="" className="h-4 w-4" />
                      Temp: {dev.temp ?? '--'}°C
                    </div>
                    <div
                      className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold uppercase tracking-wide ${
                        dev.health === 'Good'
                          ? 'bg-emerald-500/20 text-emerald-300'
                          : dev.health === 'Warning'
                          ? 'bg-amber-500/20 text-amber-200'
                          : dev.health === 'Failing'
                          ? 'bg-rose-500/20 text-rose-300'
                          : 'bg-slate-500/20 text-slate-200'
                      }`}
                    >
                      <img src={HealthIcon} alt="" className="h-4 w-4" />
                      Health: {dev.health}
                    </div>
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center gap-2 text-white/70">
                        <img src={BatteryIcon} alt="" className="h-4 w-4" />
                        <span className="font-medium">Battery: {pct}%</span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-white/10">
                        <div
                          className={`${percentToColor(pct)} h-full rounded-full transition-all duration-500`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}

          <motion.button
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.35, delay: devices.length * 0.05 }}
            onClick={() => navigate('/dashboard/add-device')}
            className="group flex min-h-[14rem] flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-white/20 bg-white/5 p-6 text-center text-white/70 backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-white/40 hover:bg-white/10"
            aria-label="Add Device"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/20 text-4xl font-light text-primary transition-all duration-300 group-hover:scale-105 group-hover:bg-primary/40">
              +
            </span>
            <span className="text-lg font-semibold text-white">Add Device</span>
            <p className="max-w-[12rem] text-xs text-white/60">
              Register a new asset and begin monitoring it instantly.
            </p>
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
}

export default Overview;
